/**
 * Rate-shop rules engine — VERBATIM port of merlin-core
 * `integrations/shipStation/v2/rateShop/rulesEngine.js` (2026-11-04). Pure functions, no I/O.
 * Used client-side for rule preview ("would this rule have matched decision X?") and validation.
 * Keep in sync with the backend; the Jest test mirrors the backend assertions.
 *
 *
 * A rule is an IFTTT-style document (see merlin-core docs/RATE-SHOP-RULES-UI-PRD.md):
 *   { id, name, enabled, priority, stopProcessing,
 *     match: { all: [cond], any: [cond] },      // cond = { field, op, value }
 *     actions: [ { type, ...params } ] }
 *
 * evaluateRules(ctx, rules) walks rules by ascending priority, applies the
 * actions of every matching rule into a "plan", and returns the plan plus a
 * per-rule trace for the audit log. Nothing here talks to ShipStation or
 * Firestore, so it is unit-testable with rulesEngine.test.js (Jest port of the backend assertions).
 */

export const CONDITION_FIELDS = [
    "storeId", "countryCode", "state", "postalPrefix", "residential", "weightOz",
    "longestSideIn", "itemCount", "orderTotal", "tag", "sku", "externalOrderId",
];

export const OPERATORS = ["eq", "neq", "in", "not_in", "gt", "gte", "lt", "lte", "between", "contains", "starts_with", "has", "not_has"];

export const ACTION_TYPES = [
    "skip", "exclude_services", "only_services", "exclude_carriers", "only_carriers",
    "exclude_groups", "force_service", "prefer_service", "add_tag",
];

const norm = (v) => (typeof v === "string" ? v.trim().toLowerCase() : v);
const asList = (v) => (Array.isArray(v) ? v : [v]).map(norm);

/** Resolve a condition field against the shipment context. Multi-valued fields return arrays. */
function fieldValue(ctx, field) {
    switch (field) {
        case "tag": return (ctx.tags || []).map(norm);
        case "sku": return (ctx.skus || []).map(norm);
        case "postalPrefix": return norm(ctx.postalCode || "");
        default: return norm(ctx[field]);
    }
}

/** True when a single condition holds for ctx. Unknown fields/ops never match. */
export function evaluateCondition(ctx, cond) {
    if (!cond || !CONDITION_FIELDS.includes(cond.field) || !OPERATORS.includes(cond.op)) return false;
    const actual = fieldValue(ctx, cond.field);
    const expected = cond.value;
    const multi = Array.isArray(actual);
    switch (cond.op) {
        case "has": return multi && asList(expected).some((v) => actual.includes(v));
        case "not_has": return multi && !asList(expected).some((v) => actual.includes(v));
        case "eq": return multi ? actual.includes(norm(expected)) : actual === norm(expected);
        case "neq": return multi ? !actual.includes(norm(expected)) : actual !== norm(expected);
        case "in": return multi ? actual.some((a) => asList(expected).includes(a)) : asList(expected).includes(actual);
        case "not_in": return multi ? !actual.some((a) => asList(expected).includes(a)) : !asList(expected).includes(actual);
        case "gt": return typeof actual === "number" && actual > Number(expected);
        case "gte": return typeof actual === "number" && actual >= Number(expected);
        case "lt": return typeof actual === "number" && actual < Number(expected);
        case "lte": return typeof actual === "number" && actual <= Number(expected);
        case "between": return typeof actual === "number" && Array.isArray(expected) && actual >= Number(expected[0]) && actual <= Number(expected[1]);
        case "contains": return multi ? actual.some((a) => String(a).includes(norm(expected))) : String(actual ?? "").includes(norm(expected));
        case "starts_with": return multi ? actual.some((a) => String(a).startsWith(norm(expected))) : String(actual ?? "").startsWith(norm(expected));
        default: return false;
    }
}

/** A rule matches when every `all` condition holds and (if present) at least one `any` holds. */
export function ruleMatches(ctx, rule) {
    const all = rule.match?.all || [];
    const any = rule.match?.any || [];
    if (!all.length && !any.length) return true; // catch-all rule
    const allOk = all.every((c) => evaluateCondition(ctx, c));
    const anyOk = any.length ? any.some((c) => evaluateCondition(ctx, c)) : true;
    return allOk && anyOk;
}

export function emptyPlan() {
    return {
        skip: null,                 // { reason, ruleId } when the shipment must be left alone
        excludeServices: new Set(), // service codes removed from the candidate list
        onlyServices: null,         // Set — when set, candidates must be in it
        excludeCarriers: new Set(), // carrier ids removed
        onlyCarriers: null,         // Set — when set, candidates must be in it
        excludeGroups: new Set(),   // CORE/MI/WALMART/AMAZON groups not rated at all
        force: null,                // { carrierId?, serviceCode, packageCode?, ruleId }
        prefer: [],                 // [{ serviceCode, carrierId?, withinAmount, withinPercent, ruleId }]
        addTags: new Set(),
    };
}

/** Apply one action to a plan, returning a NEW plan (plans are treated as immutable). */
export function applyAction(plan, action, ruleId) {
    const next = {
        ...plan,
        excludeServices: new Set(plan.excludeServices), excludeCarriers: new Set(plan.excludeCarriers),
        excludeGroups: new Set(plan.excludeGroups), addTags: new Set(plan.addTags), prefer: [...plan.prefer],
        onlyServices: plan.onlyServices ? new Set(plan.onlyServices) : null,
        onlyCarriers: plan.onlyCarriers ? new Set(plan.onlyCarriers) : null,
    };
    const list = (v) => (Array.isArray(v) ? v : [v]).filter(Boolean);
    switch (action.type) {
        case "skip": next.skip = next.skip || { reason: action.reason || "rule", ruleId }; break;
        case "exclude_services": list(action.services).forEach((s) => next.excludeServices.add(s)); break;
        case "only_services": next.onlyServices = new Set([...(next.onlyServices || []), ...list(action.services)]); break;
        case "exclude_carriers": list(action.carriers).forEach((c) => next.excludeCarriers.add(c)); break;
        case "only_carriers": next.onlyCarriers = new Set([...(next.onlyCarriers || []), ...list(action.carriers)]); break;
        case "exclude_groups": list(action.groups).forEach((g) => next.excludeGroups.add(g)); break;
        case "force_service":
            if (!next.force) next.force = { carrierId: action.carrierId || null, serviceCode: action.serviceCode, packageCode: action.packageCode || null, ruleId };
            break;
        case "prefer_service":
            next.prefer.push({ serviceCode: action.serviceCode, carrierId: action.carrierId || null, withinAmount: Number(action.withinAmount || 0), withinPercent: Number(action.withinPercent || 0), ruleId });
            break;
        case "add_tag": list(action.tag || action.tags).forEach((t) => next.addTags.add(t)); break;
        default: break; // unknown action types are ignored but recorded in the trace
    }
    return next;
}

/**
 * @param {object} ctx    shipment context (see buildRuleContext in shipmentContext.js)
 * @param {object[]} rules rule documents (enabled or not; disabled ones are traced as skipped)
 * @returns {{ plan: object, trace: object[] }}
 */
export function evaluateRules(ctx, rules) {
    const ordered = [...(rules || [])].sort((a, b) => (a.priority ?? 1000) - (b.priority ?? 1000) || String(a.id).localeCompare(String(b.id)));
    let plan = emptyPlan();
    const trace = [];
    for (const rule of ordered) {
        if (rule.enabled === false) { trace.push({ ruleId: rule.id, name: rule.name, matched: false, reason: "disabled" }); continue; }
        const matched = ruleMatches(ctx, rule);
        const applied = [];
        if (matched) {
            for (const action of rule.actions || []) {
                plan = applyAction(plan, action, rule.id);
                applied.push(action.type);
            }
        }
        trace.push({ ruleId: rule.id, name: rule.name, priority: rule.priority ?? 1000, matched, applied });
        if (matched && rule.stopProcessing) { trace.push({ ruleId: rule.id, name: rule.name, matched: true, reason: "stopProcessing" }); break; }
    }
    return { plan, trace };
}

/** Plain-JSON version of a plan for logging (Sets → arrays). */
export function serializePlan(plan) {
    return {
        skip: plan.skip,
        excludeServices: [...plan.excludeServices],
        onlyServices: plan.onlyServices ? [...plan.onlyServices] : null,
        excludeCarriers: [...plan.excludeCarriers],
        onlyCarriers: plan.onlyCarriers ? [...plan.onlyCarriers] : null,
        excludeGroups: [...plan.excludeGroups],
        force: plan.force,
        prefer: plan.prefer,
        addTags: [...plan.addTags],
    };
}
